const joinPathPattern = /\/(?:organization|team)\/join\/[^/?#\s]+/gi;
const joinQueryPattern =
  /([?&](?:code|invite(?:[_-]?id)?|[a-z_]*code)=)[^&#\s]*/gi;
const joinJsonValuePattern =
  /((?:"|')(?:code|invite(?:[_-]?id)?|[a-z_]*code)(?:"|')\s*:\s*(?:"|'))[^"']*((?:"|'))/gi;
const joinInlineValuePattern =
  /(\b(?:code|invite(?:[_-]?id)?|[a-z_]*code)\b\s*[:=]\s*)([^\s,}&]+)/gi;

const sensitiveKey = (key: string) => {
  const normalizedKey = key.toLowerCase();
  return (
    normalizedKey === 'code' ||
    normalizedKey.includes('invite') ||
    normalizedKey.endsWith('code')
  );
};

export function redactJoinTelemetryText(value: string) {
  const redactedPath = value.replace(joinPathPattern, (path) => {
    const pathSegments = path.split('/');
    return `/${pathSegments[1]}/join/[redacted]`;
  });

  try {
    const parsed = JSON.parse(redactedPath);
    if (Array.isArray(parsed) || isJoinTelemetryRecord(parsed)) {
      return JSON.stringify(redactValue(parsed));
    }
  } catch {}

  return redactedPath
    .replace(joinQueryPattern, '$1[redacted]')
    .replace(joinJsonValuePattern, '$1[redacted]$2')
    .replace(joinInlineValuePattern, '$1[redacted]');
}

export function isJoinTelemetryRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function redactValue(value: unknown): unknown {
  if (typeof value === 'string') return redactJoinTelemetryText(value);
  if (Array.isArray(value)) return value.map(redactValue);
  if (!isJoinTelemetryRecord(value)) return value;

  return redactRecord(value);
}

function redactValueInPlace(value: unknown): unknown {
  if (typeof value === 'string') return redactJoinTelemetryText(value);
  if (Array.isArray(value)) return value.map(redactValueInPlace);
  if (!isJoinTelemetryRecord(value)) return value;

  redactRecordInPlace(value);
  return value;
}

function redactRecord(data: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).map(([key, nestedValue]) => [
      key,
      sensitiveKey(key) ? '[redacted]' : redactValue(nestedValue),
    ]),
  );
}

function redactRecordInPlace(data: Record<string, unknown>) {
  for (const [key, nestedValue] of Object.entries(data)) {
    data[key] = sensitiveKey(key)
      ? '[redacted]'
      : redactValueInPlace(nestedValue);
  }
}

export function redactJoinTelemetryData(
  data: Record<string, unknown> | undefined,
) {
  if (!data) return data;
  return redactRecord(data);
}

export function redactJoinTelemetryDataInPlace(data: object | undefined) {
  if (isJoinTelemetryRecord(data)) {
    redactRecordInPlace(data);
  }
}
